import { createStore } from '@servicetitan/financing-utils-shared';
import { useState } from 'react';

// The back-end rejects a 0 or missing requested amount, so fall back to an arbitrary default (FIN-17936).
const DEFAULT_FINANCED_AMOUNT = 5000;

export interface FinancedAmountState {
    // `null` means the sale amount is not yet known (e.g. no estimate available), distinct from a
    // genuine $0 amount. Consumers that need a real, non-null number for display or editing should
    // read `editableSaleAmount` rather than this raw value (FIN-18263).
    saleAmount: number | null;
    downPayment: number;
}

function useFinancedAmount(
    initialState: FinancedAmountState = { saleAmount: null, downPayment: 0 },
) {
    const [{ saleAmount, downPayment }, setFinancedAmount] =
        useState<FinancedAmountState>(initialState);

    return {
        get financedAmount() {
            const amount = (saleAmount ?? NaN) - downPayment;
            return amount === 0 || Number.isNaN(amount) ? DEFAULT_FINANCED_AMOUNT : amount;
        },
        // Sale amount coerced to a real number for display/editing, so an unknown amount pre-fills
        // with the default instead of a misleading $0 (FIN-18263).
        get editableSaleAmount() {
            return saleAmount ?? DEFAULT_FINANCED_AMOUNT;
        },
        saleAmount,
        downPayment,
        setFinancedAmount,
    };
}

export const FinancedAmountStore = createStore(useFinancedAmount);
